import React, { useEffect, useState, useRef } from "react";
import RightModal from "../components/templates/RightModal";
import DynamicIcon from "../components/DynamicIcon";
import LoadingSection from "../components/LoadingSection";
import { selectAccountData } from "../redux/main/accountSlice";
import { getSystemsList, selectSystemsData, selectSystemsLoading } from "../redux/systems/systemsSlice";
import AddEditSystem from "../components/systemsPage/AddEditSystem";
import AddEditAddress from "../components/customersPage/AddEditAddress";
import { closeModal, resetRefreshNow, selectRightModalId, selectRightModalIsOpen, selectRightModalIsRefreshNow, selectRightModalmode, setModal } from "../redux/rightModalSlice";
import ViewSystem from "../components/systemsPage/ViewSystem";
import { useDispatch, useSelector } from "react-redux";
import { getNodeApiUrl } from "../config/api.config";
import { useColorPalette } from '../hooks/useColorPalette';
import DynamicTitle from "../components/DynamicTitle";
import DataTable from "../components/DataTable";

export default function Systems() {
    const dispatch = useDispatch();
    const palette = useColorPalette();
    const systemsList = useSelector(selectSystemsData);
    const systemsLoading = useSelector(selectSystemsLoading);
    const accountData = useSelector(selectAccountData);
    const token = localStorage.getItem('installamelo-auth-token');
    
    // Stato per la ricerca
    const [searchTerm, setSearchTerm] = useState("");
    
    // Stati per la modale di eliminazione
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [systemToDelete, setSystemToDelete] = useState(null);
    const [linkedEntities, setLinkedEntities] = useState({ maintenances: [], jobs: [] });
    const [isCheckingLinks, setIsCheckingLinks] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    
    // RightModal gestita tramite Redux
    const rightModalIsOpen = useSelector(selectRightModalIsOpen);
    const rightModalMode = useSelector(selectRightModalmode);
    const rightModalSystemId = useSelector(selectRightModalId);
    const rightModalIsRefreshNow = useSelector(selectRightModalIsRefreshNow);
    const rightModalData = useSelector(state => state.rightModal.data);
    
    // Funzione che verrà passata al componente ListPagination
    const handlePageUpdate = (page, pageSize) => {
        // Calcola l'offset corretto: page è 0-based, quindi moltiplicare per pageSize
        const start = page * pageSize;
        
        dispatch(getSystemsList({
            accountId: accountData?._id,
            token,
            search: searchTerm.length >= 3 ? searchTerm : '',
            start,
            length: pageSize
        }));
        dispatch(resetRefreshNow());
    };

    // Eseguo la chiamata iniziale quando il componente viene montato o quando cambia la ricerca
    useEffect(() => {
        if (accountData?._id) {
            dispatch(getSystemsList({
                accountId: accountData._id,
                token,
                search: searchTerm.length >= 3 ? searchTerm : '',
                start: 0, // Inizio da zero
                length: 10 // Uso la dimensione della pagina predefinita
            }));
            dispatch(resetRefreshNow());
        }
    }, [dispatch, accountData?._id, token, searchTerm]);
    
    // Avvio il refresh dei dati quando isRefreshNow della RightModal si attiva
    useEffect(() => {
        if (rightModalIsRefreshNow) handlePageUpdate(0, 10);
    }, [rightModalIsRefreshNow]);
    
    
    // Mostro i dati ottenuti dal backend nei log
    useEffect(() => {
        if (systemsList?.data) {
            console.log("Dati impianti ottenuti dal backend:", systemsList.data);
            if (systemsList.data.length > 0) {
                console.log("Struttura primo elemento:", JSON.stringify(systemsList.data[0], null, 2));
            }
        }
    }, [systemsList?.data]);
    
    // Effetto per ricerca con debounce
    const debounceRef = useRef(null);
    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        
        if (accountData?._id) {
            debounceRef.current = setTimeout(() => {
                if (searchTerm.length >= 3 || searchTerm === '') {
                    handlePageUpdate(0, 10);
                }
            }, 500);
        }
        
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [searchTerm, accountData?._id]);
    
    // Funzione per verificare i collegamenti dell'impianto prima di eliminarlo
    const handleDeleteClick = async (e, system) => {
        e.stopPropagation();
        setSystemToDelete(system);
        setIsCheckingLinks(true);
        setShowDeleteModal(true);
        
        try {
            // Verifica manutenzioni collegate
            const maintenancesRes = await fetch(
                getNodeApiUrl(`/maintenances/lists?accountId=${accountData._id}&systemId=${system.id}&length=5`),
                { headers: { 'auth-token': token } }
            );
            const maintenancesData = await maintenancesRes.json();

            // Verifica commesse collegate
            const jobsRes = await fetch(
                getNodeApiUrl(`/jobs/list?accountId=${accountData._id}&systemId=${system.id}&length=5`),
                { headers: { 'auth-token': token } }
            );
            const jobsData = await jobsRes.json();
            
            setLinkedEntities({
                maintenances: maintenancesData?.data || [],
                jobs: jobsData?.data || []
            });
        } catch (error) {
            console.error('Errore nel controllo collegamenti:', error);
            setLinkedEntities({ maintenances: [], jobs: [] });
        } finally {
            setIsCheckingLinks(false);
        }
    };
    
    // Funzione per confermare l'eliminazione
    const confirmDelete = async () => {
        if (!systemToDelete) return;
        
        setIsDeleting(true);
        try {
            const response = await fetch(
                getNodeApiUrl('/systems'),
                {
                    method: 'DELETE',
                    headers: {
                        'auth-token': token,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ systemId: systemToDelete.id })
                }
            );
            
            if (response.ok) {
                // Aggiorna la lista
                handlePageUpdate(0, 10);
                setShowDeleteModal(false);
                setSystemToDelete(null);
            } else {
                const errorData = await response.json();
                alert(`Errore durante l'eliminazione: ${errorData.message || 'Errore sconosciuto'}`);
            }
        } catch (error) {
            console.error('Errore eliminazione impianto:', error);
            alert('Errore durante l\'eliminazione dell\'impianto');
        } finally {
            setIsDeleting(false);
        }
    };
    
    // Chiudi modale eliminazione
    const closeDeleteModal = () => {
        setShowDeleteModal(false);
        setSystemToDelete(null);
        setLinkedEntities({ maintenances: [], jobs: [] });
    };
    
    if (!palette) {
        return <LoadingSection isFixed />;
    }
    
    return (
        <div className="page">
            {systemsLoading && <LoadingSection isFixed />}
            <DynamicTitle title="Impianti" />
            
            <div className="flex justify-between items-center mb-4">
                <h1 className="text-2xl font-bold" style={{ color: palette.titleColor || palette.textColor }}>Impianti</h1>
                <button 
                    onClick={() => {
                        // Prima chiudiamo completamente qualsiasi modale aperta
                        if (rightModalIsOpen) {
                            dispatch(closeModal());
                            // Dopo un breve ritardo per consentire l'animazione di chiusura
                            setTimeout(() => {
                                dispatch(setModal({ mode: 'add', percentuageWidth: 50 }));
                            }, 650); // Leggermente più lungo dell'animazione (600ms)
                        } else {
                            // Se non c'è una modale aperta, apriamo direttamente
                            dispatch(setModal({ mode: 'add', percentuageWidth: 50 }));
                        }
                    }}
                    className="flex items-center gap-2 rounded"
                    style={{ 
                        backgroundColor: palette.buttonPrimaryBg || palette.primary, 
                        color: palette.buttonPrimaryText || palette.textOnPrimary, 
                        padding: '0.5rem 1rem', 
                        borderRadius: '0.375rem',
                        border: `1px solid ${palette.buttons?.primary?.border || 'transparent'}`
                    }}
                    onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = palette.buttonPrimaryHover || palette.primary;
                    }}
                    onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = palette.buttonPrimaryBg || palette.primary;
                    }}
                >
                    <DynamicIcon iconClass="fas fa-plus" />
                    Nuovo Impianto
                </button>
            </div>
            
            <div className="box" style={{ backgroundColor: palette.boxBg || '#ffffff', color: palette.boxText || '#000000', borderColor: palette.boxBorder || '#e5e7eb' }}>
                {/* Barra di ricerca */}
                <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
                    <div className="relative w-full">
                        <input 
                            type="text" 
                            className="text-sm rounded-lg block w-full p-2.5 pr-10"
                            style={{
                                backgroundColor: palette.formInputBg || '#f9fafb',
                                borderColor: palette.formBorderColor || '#d1d5db',
                                color: palette.formInputText || '#111827',
                                outlineColor: palette.primary
                            }}
                            placeholder="Cerca impianto..." 
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        {searchTerm ? (
                            <button 
                                className="absolute inset-y-0 end-0 flex items-center pe-3"
                                onClick={() => setSearchTerm('')}
                            >
                                <DynamicIcon iconClass="fa-solid fa-times" style={{ color: palette.iconColor || palette.textColor }} />
                            </button>
                        ) : (
                            <div className="absolute inset-y-0 end-0 flex items-center pe-3 pointer-events-none">
                                <DynamicIcon iconClass="fa-solid fa-search" style={{ color: palette.iconColor || palette.textColor }} />
                            </div>
                        )}
                    </div>
                </div>
                
                <div className="overflow-x-auto">
                    <DataTable
                        columns={[
                            { 
                                id: 'title', 
                                title: 'NOME IMPIANTO',
                                width: '20%',
                                render: (system) => (
                                    <div className="text-left uppercase">
                                        {system.title || system.name || '-'}
                                    </div>
                                )
                            },
                            {
                                id: 'customer',
                                title: 'CLIENTE',
                                width: '20%',
                                render: (system) => (
                                    <div className="text-left uppercase flex items-center gap-1 flex-wrap">
                                        <span>{system.customerLabel || system.customer?.name || '-'}</span>
                                        {system.customerIsProspect && (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700 normal-case">
                                                Prospect
                                            </span>
                                        )}
                                    </div>
                                )
                            },
                            { 
                                id: 'address', 
                                title: 'INDIRIZZO',
                                width: '20%',
                                render: (system) => (
                                    <div className="text-left truncate uppercase">
                                        {system.address || '-'}
                                    </div>
                                )
                            },
                            { 
                                id: 'types', 
                                title: 'TIPO',
                                width: '25%',
                                render: (system) => (
                                    <div className="text-left flex flex-wrap items-center gap-2">
                                        {system.types && system.types.map((type, idx) => (
                                            <div key={`type-${type._id || idx}`} className="flex items-center gap-1">
                                                <span className="rounded bg-gray-200 px-2 py-1 text-sm text-gray-800 uppercase">
                                                    {type.typeLabel}
                                                </span>
                                                {type.expirationWarrantyDate && (
                                                    <span 
                                                        className={`ml-1 flex items-center justify-center rounded-full w-5 h-5 text-white ${new Date(type.expirationWarrantyDate) > new Date() ? 'bg-green-600' : 'bg-red-600'}`}
                                                        title={new Date(type.expirationWarrantyDate) > new Date() ? 'In garanzia' : 'Garanzia scaduta'}
                                                    >
                                                        {new Date(type.expirationWarrantyDate) > new Date() ? 
                                                            <DynamicIcon iconClass="fas fa-check text-xs" /> : 
                                                            <DynamicIcon iconClass="fas fa-exclamation-triangle text-xs" />}
                                                    </span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )
                            },
                            { 
                                id: 'actions', 
                                title: 'AZIONI',
                                width: '15%',
                                render: (system) => (
                                    <div className="flex gap-3 justify-center">
                                        <button 
                                            style={{ color: 'inherit' }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                dispatch(setModal({
                                                    mode: 'view',
                                                    id: system.id,
                                                    percentuageWidth: 90
                                                }));
                                            }}
                                        >
                                            <DynamicIcon iconClass="fa-solid fa-eye" />
                                        </button>
                                        <button 
                                            style={{ color: 'inherit' }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                dispatch(setModal({
                                                    mode: 'edit',
                                                    id: system.id,
                                                    percentuageWidth: 50
                                                }));
                                            }}
                                        >
                                            <DynamicIcon iconClass="fa-solid fa-edit" />
                                        </button>
                                        <button 
                                            style={{ color: 'inherit' }}
                                            onClick={(e) => handleDeleteClick(e, system)}
                                            title="Elimina"
                                        >
                                            <DynamicIcon iconClass="fa-solid fa-trash" />
                                        </button>
                                    </div>
                                )
                            }
                        ]}
                        data={systemsList?.data || []}
                        loading={systemsLoading}
                        totalRecords={systemsList?.recordsFiltered || 0}
                        pageSize={10}
                        onPageChange={(page, pageSize) => handlePageUpdate(page, pageSize)}
                        hasSearch={true}
                        onSearch={(term) => setSearchTerm(term)}
                        initialSearchTerm={searchTerm}
                        emptyMessage="Nessun impianto trovato"
                        renderCustomRow={(system, index, isEven, tableStyles) => {
                            // Funzione per gestire il click sulla riga
                            const handleRowClick = () => {
                                dispatch(setModal({
                                    isOpen: true,
                                    mode: 'view',
                                    id: system.id,
                                    percentuageWidth: 90
                                }));
                            };
                            
                            return (
                                <div 
                                    key={system.id}
                                    className="data-table-row cursor-pointer"
                                    style={{
                                        backgroundColor: isEven ? tableStyles.evenRow.backgroundColor : tableStyles.oddRow.backgroundColor,
                                        color: isEven ? tableStyles.evenRow.color : tableStyles.oddRow.color,
                                    }}
                                    onClick={handleRowClick}
                                >
                                    {/* Colonna NOME IMPIANTO */}
                                    <div className="data-table-cell" style={{ width: '20%' }} data-label="NOME IMPIANTO">
                                        <div className="text-left uppercase">
                                            {system.title || system.name || '-'}
                                        </div>
                                    </div>
                                    
                                    {/* Colonna CLIENTE */}
                                    <div className="data-table-cell" style={{ width: '20%' }} data-label="CLIENTE">
                                        <div className="text-left uppercase flex items-center gap-1 flex-wrap">
                                            <span>{system.customerLabel || system.customer?.name || '-'}</span>
                                            {system.customerIsProspect && (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700 normal-case">
                                                    Prospect
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    
                                    {/* Colonna INDIRIZZO */}
                                    <div className="data-table-cell" style={{ width: '20%' }} data-label="INDIRIZZO">
                                        <div className="text-left truncate">
                                            {system.address || '-'}
                                        </div>
                                    </div>
                                    
                                    {/* Colonna TIPO */}
                                    <div className="data-table-cell" style={{ width: '25%' }} data-label="TIPO">
                                        <div className="text-left flex flex-wrap items-center gap-2">
                                            {system.types && system.types.map((type, idx) => (
                                                <div key={`type-${type._id || idx}`} className="flex items-center gap-1">
                                                    <span className="rounded bg-gray-200 px-2 py-1 text-sm text-gray-800">
                                                        {type.typeLabel}
                                                    </span>
                                                    {type.expirationWarrantyDate && (
                                                        <span 
                                                            className={`ml-1 flex items-center justify-center rounded-full w-5 h-5 text-white ${new Date(type.expirationWarrantyDate) > new Date() ? 'bg-green-600' : 'bg-red-600'}`}
                                                            title={new Date(type.expirationWarrantyDate) > new Date() ? 'In garanzia' : 'Garanzia scaduta'}
                                                        >
                                                            {new Date(type.expirationWarrantyDate) > new Date() ? 
                                                                <DynamicIcon iconClass="fas fa-check text-xs" /> : 
                                                                <DynamicIcon iconClass="fas fa-exclamation-triangle text-xs" />}
                                                        </span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    
                                    {/* Colonna AZIONI */}
                                    <div className="data-table-cell" style={{ width: '15%' }} data-label="AZIONI">
                                        <div className="flex gap-3 justify-center">
                                            <button 
                                                style={{ color: isEven ? tableStyles.evenRow.color : tableStyles.oddRow.color }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    dispatch(setModal({
                                                        mode: 'view',
                                                        id: system.id,
                                                        percentuageWidth: 90
                                                    }));
                                                }}
                                            >
                                                <DynamicIcon iconClass="fa-solid fa-eye" />
                                            </button>
                                            <button 
                                                style={{ color: isEven ? tableStyles.evenRow.color : tableStyles.oddRow.color }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    dispatch(setModal({
                                                        mode: 'edit',
                                                        id: system.id,
                                                        percentuageWidth: 50
                                                    }));
                                                }}
                                            >
                                                <DynamicIcon iconClass="fa-solid fa-edit" />
                                            </button>
                                            <button 
                                                style={{ color: isEven ? tableStyles.evenRow.color : tableStyles.oddRow.color }}
                                                onClick={(e) => handleDeleteClick(e, system)}
                                                title="Elimina"
                                            >
                                                <DynamicIcon iconClass="fa-solid fa-trash" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        }}
                    />
                </div>
            </div>
            
            {/* RIGHT MODAL per inserimento o modifica impianto */}
            {rightModalIsOpen && (
                <RightModal>
                    {(rightModalMode === 'add' || rightModalMode === 'edit') && (
                        <AddEditSystem systemId={rightModalSystemId} />
                    )}
                    {rightModalMode === 'view' && rightModalSystemId && (
                        <>
                            {console.log('Apertura ViewSystem con ID:', rightModalSystemId)}
                            <ViewSystem systemId={rightModalSystemId} />
                        </>
                    )}
                    {rightModalMode === 'add_address' && (
                        <AddEditAddress 
                            customerId={rightModalData?.customerId}
                            customerData={rightModalData?.customerData}
                        />
                    )}
                </RightModal>
            )}
            
            {/* MODALE CONFERMA ELIMINAZIONE */}
            {showDeleteModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto">
                        <h3 className="text-xl font-bold mb-4 text-gray-800">
                            Conferma eliminazione impianto
                        </h3>
                        
                        {isCheckingLinks ? (
                            <div className="text-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                                <p className="text-gray-600">Verifica collegamenti in corso...</p>
                            </div>
                        ) : (
                            <>
                                <p className="text-gray-600 mb-4">
                                    Stai per eliminare l&apos;impianto: <strong>{systemToDelete?.label || systemToDelete?.name}</strong>
                                </p>
                                
                                {(linkedEntities.maintenances.length > 0 || linkedEntities.jobs.length > 0) && (
                                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                                        <p className="text-yellow-800 font-medium mb-2">
                                            ⚠️ Attenzione: questo impianto è collegato alle seguenti entità:
                                        </p>
                                        
                                        {linkedEntities.maintenances.length > 0 && (
                                            <div className="mb-3">
                                                <p className="text-yellow-700 font-medium text-sm">Manutenzioni ({linkedEntities.maintenances.length}):</p>
                                                <ul className="list-disc list-inside text-sm text-yellow-700">
                                                    {linkedEntities.maintenances.slice(0, 5).map((m, i) => (
                                                        <li key={i}>{m.code || m._id} - {m.customerLabel || 'N/D'}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                        
                                        {linkedEntities.jobs.length > 0 && (
                                            <div>
                                                <p className="text-yellow-700 font-medium text-sm">Commesse ({linkedEntities.jobs.length}):</p>
                                                <ul className="list-disc list-inside text-sm text-yellow-700">
                                                    {linkedEntities.jobs.slice(0, 5).map((j, i) => (
                                                        <li key={i}>{j.code || j._id} - {j.customerLabel || 'N/D'}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                        
                                        <p className="text-yellow-800 text-sm mt-3">
                                            Eliminando l&apos;impianto, queste entità perderanno il collegamento logico.
                                        </p>
                                    </div>
                                )}
                                
                                <p className="text-gray-600 mb-6">
                                    Sei sicuro di voler procedere con l&apos;eliminazione?
                                </p>
                                
                                <div className="flex justify-end gap-3">
                                    <button
                                        onClick={closeDeleteModal}
                                        className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                                        disabled={isDeleting}
                                    >
                                        Annulla
                                    </button>
                                    <button
                                        onClick={confirmDelete}
                                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                                        disabled={isDeleting}
                                    >
                                        {isDeleting ? 'Eliminazione...' : 'Elimina'}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
